import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ArrowRight } from 'lucide-react';
import { EmailTimeline } from './EmailTimeline';
import { EmailTemplatePicker } from './EmailTemplatePicker';

/**
 * Simplified sequence editor with step-by-step wizard
 */
export function SimplifiedSequenceEditor({ 
  sequence,
  templates = [],
  onSave,
  onClose
}) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState(sequence?.name || '');
  const [trigger, setTrigger] = useState(sequence?.trigger || 'welcome');
  const [status, setStatus] = useState(sequence?.status || 'draft');
  const [emails, setEmails] = useState(sequence?.emails || []);
  const [emailCount, setEmailCount] = useState(sequence?.emails?.length || 1);

  // Initialize emails based on count when creating new sequence
  useEffect(() => {
    if (!sequence && emailCount > 0) {
      // If we have fewer emails than the count, add empty ones
      if (emails.length < emailCount) {
        const newEmails = Array.from({ length: emailCount }, (_, i) => {
          // Keep existing email if it exists, otherwise create new
          return emails[i] || {
            templateId: '',
            delay: 0,
            order: i
          };
        });
        setEmails(newEmails);
      } else if (emails.length > emailCount) {
        // If we have more emails than count, trim the array
        setEmails(emails.slice(0, emailCount));
      }
    }
  }, [emailCount, sequence]);

  const triggerOptions = [
    { value: 'welcome', label: 'When someone subscribes' },
    { value: 'purchase', label: 'When someone buys something' },
    { value: 'booking-confirmed', label: 'When someone books a session' },
    { value: 'course-enrolled', label: 'When someone enrolls in a course' },
    { value: 'membership-activated', label: 'When someone joins membership' },
    { value: 'manual', label: 'Send manually' }
  ];

  const timingPresets = [
    { label: 'Now', days: 0 },
    { label: '1 day later', days: 1 },
    { label: '3 days later', days: 3 },
    { label: '1 week later', days: 7 },
    { label: '2 weeks later', days: 14 }
  ];

  const handleEmailCountChange = (count) => {
    setEmailCount(count);
    // Update emails array
    const newEmails = Array.from({ length: count }, (_, i) => {
      if (emails[i]) {
        return emails[i];
      }
      return {
        templateId: '',
        delay: 0,
        order: i
      };
    });
    setEmails(newEmails.slice(0, count));
  };

  const handleUpdateEmail = (index, updates) => {
    const newEmails = [...emails];
    newEmails[index] = { ...newEmails[index], ...updates };
    setEmails(newEmails);
  };

  const handleTimingPreset = (index, days) => {
    const hours = days * 24;
    handleUpdateEmail(index, { delay: hours });
  };

  const handleSave = () => {
    if (!name || emails.length === 0) {
      alert('Please fill in name and configure at least one email');
      return;
    }

    // Validate all emails have templates
    const hasEmptyTemplates = emails.some(e => !e.templateId);
    if (hasEmptyTemplates) {
      alert('Please select a template for each email');
      return;
    }

    onSave({
      name,
      trigger,
      status,
      emails: emails.map((email, index) => ({
        ...email,
        order: index
      }))
    });
  };

  const daysToHours = (days) => days * 24;
  const hoursToDays = (hours) => Math.floor(hours / 24);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-none sm:rounded-2xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              {sequence ? 'Edit Sequence' : 'New Email Series'}
            </h2>
            <div className="flex items-center gap-2 mt-2">
              {[1, 2, 3, 4].map((s) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s ? 'bg-[#c5a059] text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {s}
                  </div>
                  {s < 4 && (
                    <div className={`w-12 h-0.5 ${
                      step > s ? 'bg-[#c5a059]' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Step 1: Name and Trigger */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">When should this send?</h3>
                <div className="space-y-3">
                  {triggerOptions.map(option => (
                    <label
                      key={option.value}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                        trigger === option.value
                          ? 'border-[#c5a059] bg-[#c5a059]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        value={option.value}
                        checked={trigger === option.value}
                        onChange={(e) => setTrigger(e.target.value)}
                        className="mr-3"
                      />
                      <span className="text-gray-800">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Series Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Welcome Series"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  disabled={!name || !trigger}
                  className="flex items-center gap-2 px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Number of Emails */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">How many emails?</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[1, 3, 5, 7].map(count => (
                    <button
                      key={count}
                      onClick={() => handleEmailCountChange(count)}
                      className={`p-4 border-2 rounded-lg text-center transition ${
                        emailCount === count
                          ? 'border-[#c5a059] bg-[#c5a059]/5 text-[#c5a059] font-semibold'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl font-bold mb-1">{count}</div>
                      <div className="text-xs text-gray-600">email{count !== 1 ? 's' : ''}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={emailCount < 1}
                  className="flex items-center gap-2 px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Select Templates */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Select email templates</h3>
                <div className="space-y-4">
                  {emails.map((email, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-800">Email {index + 1}</h4>
                      </div>
                      <EmailTemplatePicker
                        templates={templates}
                        selectedTemplateId={email.templateId}
                        onSelect={(templateId) => handleUpdateEmail(index, { templateId })}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(4)}
                  disabled={emails.some(e => !e.templateId)}
                  className="flex items-center gap-2 px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Set Timing */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">When should each email send?</h3>
                <div className="space-y-4">
                  {emails.map((email, index) => {
                    const currentDays = hoursToDays(email.delay || 0);
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-800">Email {index + 1}</h4>
                          <span className="text-sm text-gray-500">
                            {getTemplateName(email.templateId)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {timingPresets.map(preset => (
                            <button
                              key={preset.label}
                              onClick={() => handleTimingPreset(index, preset.days)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                currentDays === preset.days
                                  ? 'bg-[#c5a059] text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                        <div className="mt-3">
                          <label className="block text-xs text-gray-600 mb-1">Or enter days:</label>
                          <input
                            type="number"
                            value={currentDays}
                            onChange={(e) => {
                              const days = parseInt(e.target.value) || 0;
                              handleUpdateEmail(index, { delay: daysToHours(days) });
                            }}
                            min="0"
                            className="w-24 px-3 py-1.5 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Timeline Preview */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-gray-800 mb-3">Preview Timeline</h4>
                <EmailTimeline emails={emails} templates={templates} />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(3)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
                >
                  Back
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
                >
                  Save Sequence
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function getTemplateName(templateId) {
    if (!templateId) return 'No template selected';
    const template = templates.find(t => t.id === templateId);
    return template?.name || 'Unknown template';
  }
}

